import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../assets/contexts/ThemeContext';
import './ScrollToTopBtn.css';

/**
 * ScrollToTop 버튼 컴포넌트
 * 기존 jQuery scrollup.js 플러그인을 React로 대체
 * 모든 해상도에서 동일하게 동작
 * #root 또는 body에 직접 렌더링하여 중복 방지
 * 2가지 유형 지원: default, with-text
 */
const ScrollToTopBtn = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [container, setContainer] = useState(null);
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || (typeof window !== 'undefined' && window.__themeConfig) || {};
  const buttonType = themeConfig.scrollToTopButtonType || 'default';

  // 스크롤 위치 확인 및 버튼 표시/숨김 처리
  const handleScroll = useCallback(() => {
    // alliance 페이지인지 확인
    const isAlliancePage = document.body.classList.contains('alliance-page-active');
    let scrollTop = 0;

    if (isAlliancePage) {
      // alliance 페이지인 경우 .alliance-page__content의 스크롤 확인
      const contentElement = document.querySelector('.alliance-page__content');
      if (contentElement) {
        scrollTop = contentElement.scrollTop || 0;
      }
    } else {
      // 일반 페이지인 경우 window 스크롤 확인
      scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        window.scrollY ||
        (document.documentElement && document.documentElement.scrollTop) ||
        0;
    }

    const scrollDistance = 300; // 기본 scrollDistance (기존 플러그인과 동일)

    if (scrollTop > scrollDistance) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, []);

  // 컨테이너 설정 및 기존 버튼 제거
  useEffect(() => {
    // body를 컨테이너로 사용 (모든 해상도에서 동일하게 작동)
    const bodyElement = document.body;
    setContainer(bodyElement);

    // 기존 jQuery로 생성된 #scrollUp 버튼 제거 (React 컴포넌트가 아닌 것들)
    const removeExistingButtons = () => {
      const existingButtons = document.querySelectorAll('#scrollUp');
      existingButtons.forEach((btn) => {
        // React 컴포넌트가 아닌 버튼만 제거
        if (!btn.hasAttribute('data-react-component')) {
          btn.remove();
        }
      });
    };

    // 즉시 제거
    removeExistingButtons();

    // DOM이 완전히 로드된 후 다시 확인
    if (document.readyState === 'complete') {
      removeExistingButtons();
    } else {
      window.addEventListener('load', removeExistingButtons);
    }

    // 주기적으로 확인 (jQuery 플러그인이 나중에 생성할 수 있음)
    const checkInterval = setInterval(() => {
      removeExistingButtons();
    }, 1000);

    return () => {
      window.removeEventListener('load', removeExistingButtons);
      clearInterval(checkInterval);
    };
  }, []);

  // 스크롤 이벤트 리스너 등록
  useEffect(() => {
    // 모바일에서도 확실하게 작동하도록 여러 번 확인
    const checkInitialScroll = () => {
      // 즉시 확인
      handleScroll();

      // requestAnimationFrame으로 확인 (브라우저 렌더링 완료 후)
      requestAnimationFrame(() => {
        handleScroll();
        requestAnimationFrame(() => {
          handleScroll();
        });
      });
    };

    // DOM이 완전히 로드된 후 확인
    if (document.readyState === 'complete') {
      checkInitialScroll();
    } else {
      window.addEventListener('load', checkInitialScroll);
    }

    // alliance 페이지인지 확인
    const isAlliancePage = document.body.classList.contains('alliance-page-active');
    const contentElement = isAlliancePage ? document.querySelector('.alliance-page__content') : null;

    // 스크롤 이벤트 리스너 등록
    if (isAlliancePage && contentElement) {
      // alliance 페이지인 경우 .alliance-page__content에 스크롤 이벤트 리스너 등록
      contentElement.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      // 일반 페이지인 경우 window에 스크롤 이벤트 리스너 등록
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    // 리사이즈 이벤트도 감지 (모바일에서 화면 회전 시 대응)
    window.addEventListener('resize', handleScroll, { passive: true });

    // 모바일에서 스크롤 이벤트가 제대로 감지되지 않을 수 있으므로 주기적으로 확인
    const intervalId = setInterval(() => {
      handleScroll();
    }, 500); // 500ms마다 확인

    return () => {
      if (isAlliancePage && contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('load', checkInitialScroll);
      clearInterval(intervalId);
    };
  }, [handleScroll]);

  // 상단으로 스크롤
  const scrollToTop = useCallback((e) => {
    // 이벤트가 전달된 경우에만 preventDefault (모바일 터치 이벤트는 제외)
    if (e && e.type !== 'touchstart' && e.type !== 'touchend') {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isScrolling) return;

    setIsScrolling(true);

    // alliance 페이지인지 확인
    const isAlliancePage = document.body.classList.contains('alliance-page-active');
    const contentElement = isAlliancePage ? document.querySelector('.alliance-page__content') : null;

    if (isAlliancePage && contentElement) {
      // alliance 페이지인 경우 .alliance-page__content 스크롤
      const startPosition = contentElement.scrollTop || 0;
      const startTime = performance.now();
      const duration = 900;

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = progress;
        const currentPosition = startPosition * (1 - ease);

        contentElement.scrollTop = currentPosition;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          contentElement.scrollTop = 0;
          setIsScrolling(false);
        }
      };

      requestAnimationFrame(animateScroll);
    } else {
      // 일반 페이지인 경우 window 스크롤
      const htmlElement = document.documentElement;
      const bodyElement = document.body;

      const startPosition =
        window.pageYOffset ||
        htmlElement.scrollTop ||
        bodyElement.scrollTop ||
        window.scrollY ||
        0;

      const startTime = performance.now();
      const duration = 900;

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = progress;
        const currentPosition = startPosition * (1 - ease);

        window.scrollTo(0, currentPosition);
        htmlElement.scrollTop = currentPosition;
        bodyElement.scrollTop = currentPosition;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          window.scrollTo(0, 0);
          htmlElement.scrollTop = 0;
          bodyElement.scrollTop = 0;
          setIsScrolling(false);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, [isScrolling]);

  // 항상 DOM에 렌더링하고 display로 제어 (기존 플러그인과 동일한 방식)
  // 모바일에서도 확실하게 작동하도록 항상 DOM에 존재
  // body에 직접 Portal로 렌더링하여 중복 방지
  if (!container) {
    return null;
  }

  // 버튼 유형에 따른 스타일 및 내용 결정
  const getButtonContent = () => {
    const themeColor = themeConfig.themeColor || '#e97730';

    switch (buttonType) {
      case 'with-text':
        return (
          <div className="scroll-to-top-btn-with-text-container">
            <i
              className="ti-arrow-up scroll-to-top-btn-with-text-icon"
              style={{ color: themeColor }}
            ></i>
            <span
              className="scroll-to-top-btn-with-text-label"
              style={{ color: themeColor }}
            >TOP</span>
          </div>
        );
      default: // 'default'
        // 이전: ti 아이콘 폰트 사용
        // return <i className="ti-arrow-up"></i>;
        // viewBox 24x24 기준 세로 중앙(y=12)에 맞춘 화살표 + 원 안에서 flex로 가운데 정렬
        const topSvg = (
          <div className="scroll-to-top-btn-icon-wrap">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 16L12 8L20 16" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
        return topSvg;
    }
  };

  const getButtonStyle = () => {
    const baseStyle = {
      display: isVisible ? 'block' : 'none',
      opacity: isVisible ? 0.8 : 0,
      pointerEvents: isVisible ? 'auto' : 'none',
    };

    return baseStyle;
  };

  const getButtonClassName = () => {
    const baseClass = 'scroll-to-top-btn';
    if (buttonType === 'with-text') {
      return `${baseClass} scroll-to-top-btn-with-text`;
    }
    return baseClass;
  };

  const buttonElement = (
    <a
      id="scrollUp"
      href="#top"
      data-react-component="true"
      className={getButtonClassName()}
      onClick={scrollToTop}
      onTouchStart={(e) => {
        // 모바일 터치 이벤트 처리 - preventDefault 제거하여 스크롤이 작동하도록
        scrollToTop(e);
      }}
      style={getButtonStyle()}
      aria-label="맨 위로 스크롤"
    >
      {getButtonContent()}
    </a>
  );

  // body에 Portal로 렌더링 (모든 해상도에서 동일한 위치)
  return createPortal(buttonElement, container);
};

export default ScrollToTopBtn;

